import {getActiveBranch} from './messageTree';
import type {AgentChatState, MessageNode, Thread} from './thread';

export interface ActiveThreadLoadState {
  activeThreadId: string | null;
  error: string | null;
  isLoading: boolean;
}

export function selectThreads(state: AgentChatState): Record<string, Thread> {
  return state.threads;
}

export function selectActiveThreadId(state: AgentChatState): string | null {
  return state.activeThreadId;
}

export function selectActiveThread(state: AgentChatState): Thread | undefined {
  return state.activeThreadId ? state.threads[state.activeThreadId] : undefined;
}

export function selectActiveThreadLoadState(
  state: AgentChatState
): ActiveThreadLoadState {
  return {
    activeThreadId: state.activeThreadId,
    error: state.activeThreadLoadError,
    isLoading: state.isActiveThreadLoading,
  };
}

export function selectThreadBranch(
  state: AgentChatState,
  threadId: string,
  upToNodeId?: string
): MessageNode[] {
  const thread = state.threads[threadId];
  return thread ? getActiveBranch(thread.messageTree, upToNodeId) : [];
}

export function selectActiveBranch(state: AgentChatState): MessageNode[] {
  return state.activeThreadId
    ? selectThreadBranch(state, state.activeThreadId)
    : [];
}

export function selectIsRunning(state: AgentChatState): boolean {
  const thread = selectActiveThread(state);
  return thread?.status === 'running';
}

export function selectThreadById(
  state: AgentChatState,
  threadId: string
): Thread | undefined {
  return state.threads[threadId];
}

/**
 * selectHistoryItems projects a list of thread ids to their Thread entries,
 * dropping ids that are missing from the store or hidden by a pending or
 * confirmed delete.
 */
export function selectHistoryItems(
  state: AgentChatState,
  ids: readonly string[]
): Thread[] {
  const items: Thread[] = [];
  for (const id of ids) {
    if (state.pendingDeletes.has(id) || state.deletedThreadIds.has(id)) {
      continue;
    }

    const thread = state.threads[id];
    if (thread) {
      items.push(thread);
    }
  }
  return items;
}

/**
 * selectThreadDisplayTitle returns the renderable title for a thread,
 * preferring the pending-rename overlay when present, then the stored
 * title, then the prompt-snippet fallback, then a generic label.
 */
export function selectThreadDisplayTitle(
  state: AgentChatState,
  threadId: string
): string {
  const thread = state.threads[threadId];
  const stored = state.pendingRenames[threadId]?.title ?? thread?.title;
  return stored?.trim() || thread?.promptSnippet || 'Untitled conversation';
}

/**
 * selectThreadIdByRootTurnId finds the thread ID for a given root turn ID.
 * Returns the thread ID if found, or null if no thread matches.
 */
export function selectThreadIdByRootTurnId(
  state: AgentChatState,
  rootTurnId: string
): string | null {
  // Threads loaded via a thread-switch from chat history will set their unique
  // ID to the root turn ID.
  if (state.threads[rootTurnId]) {
    return rootTurnId;
  }

  // New threads don't have a turn ID at time of creation within the UI, so
  // their associated root turn IDs need to be individually checked.
  for (const thread of Object.values(state.threads)) {
    if (thread.rootTurnId === rootTurnId) {
      return thread.id;
    }
  }

  return null;
}
